import {useMemo} from 'react'
import {StyleSheet, View} from 'react-native'
import {SystemBars} from 'react-native-edge-to-edge'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {AtUri} from '@atproto/api'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
  type NavigationProp,
} from '#/lib/routes/types'
import {useFlashbookByUriQuery} from '#/state/queries/flashbook'
import {useSession} from '#/state/session'
import {useEnableMinimalShellModeForScreen} from '#/state/shell'
import {atoms as a, ThemeProvider} from '#/alf'
import {FlashBookPlayer} from '#/components/flashbooks/FlashBookPlayer'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {type FlashBookModel} from '#/types/flashbook'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'FlashBookPlay'>

export function FlashBookPlayScreen({route, navigation}: Props) {
  const {t: l} = useLingui()
  const nav = useNavigation<NavigationProp>()
  useEnableMinimalShellModeForScreen()
  const insets = useSafeAreaInsets()
  const {currentAccount} = useSession()

  const {flashbookid} = route.params
  /*
   * Whether the way in asked for the learner's page rather than the cover.
   *
   * Coerced rather than trusted, because this param travels in a URL: the browser's
   * own query string hands every value back as *text*, so `resume=true` arrives as
   * the string `'true'` — and a plain truthiness check would read `resume=false` as
   * a request to resume.
   */
  const openAtPlace = String(route.params.resume) === 'true'
  /*
   * Whether the way in asked for the *class report* rather than the book.
   *
   * Coerced the same way, and for the same reason: a query string hands every
   * value back as text, so `classReport=false` must not read as a request for the
   * teacher's screen. Who is allowed to have it is decided below, against the
   * session — the param only says what was asked for.
   */
  const openAtClassReport = String(route.params.classReport) === 'true'
  const uri = useMemo(() => {
    // Accept a full at:// URI or a bare rkey (resolved against the current
    // account's did for convenience).
    if (flashbookid.startsWith('at://')) return flashbookid
    if (flashbookid.startsWith('did:')) return flashbookid
    if (!currentAccount) return undefined
    return `at://${currentAccount.did}/app.bsky.feed.flashbook/${flashbookid}`
  }, [flashbookid, currentAccount])

  const {data, isFetched} = useFlashbookByUriQuery(uri)

  const book: FlashBookModel | null = useMemo(() => {
    if (!data) return null
    const atUri = new AtUri(data.uri)
    return {
      id: data.uri,
      title: data.title ?? 'Untitled book',
      description: data.description ?? '',
      creatorId: atUri.hostname,
      themeId: data.themeId ?? 'storybook',
      status: data.status ?? 'published',
      cover: data.cover ?? {kind: 'image'},
      pages: data.pages,
      behaviorSettings: data.behaviorSettings ?? {},
      metadata: {createdAt: '', updatedAt: ''},
      likeCount: 0,
      likedByMe: false,
    }
  }, [data])

  return (
    <ThemeProvider theme="dark">
      <Layout.Screen
        noInsetTop
        style={{backgroundColor: 'black', flex: 1, overflow: 'hidden'}}>
        <SystemBars style={{statusBar: 'light', navigationBar: 'light'}} />
        {book ? (
          <FlashBookPlayer
            book={book}
            /* Where the reader goes in: the page they left, when the caller
               promised one and this device still holds it (see the param). */
            openAtPlace={openAtPlace}
            /*
             * The teacher's way in, asked for from a feed card. It is a request and
             * nothing more: the player shows the report only when `isAuthor` agrees,
             * so a learner who types this into a URL gets their book.
             */
            openAtClassReport={openAtClassReport}
            /*
             * Whether this reader is the book's author — and so has a class.
             *
             * Compared against the signed-in account rather than taken from the
             * route, because the route is public: anybody can open anybody's book,
             * and the class report is the one ending only the person who set the
             * book has a use for. An author reading their own book still gets the
             * learner's report first; this only adds the door.
             */
            isAuthor={!!currentAccount && book.creatorId === currentAccount.did}
            onClose={() => {
              if (navigation.canGoBack()) {
                navigation.goBack()
              } else {
                nav.navigate('Home')
              }
            }}
          />
        ) : (
          <View
            style={[
              a.flex_1,
              a.align_center,
              a.justify_center,
              {paddingTop: insets.top},
            ]}>
            {isFetched ? (
              <Text style={styles.msg}>{l`Could not find this book.`}</Text>
            ) : (
              <Text style={styles.msg}>{l`Loading book…`}</Text>
            )}
          </View>
        )}
      </Layout.Screen>
    </ThemeProvider>
  )
}

const styles = StyleSheet.create({
  msg: {color: 'rgba(255,255,255,0.6)', fontSize: 14},
})
